import React, { useState } from 'react';
import { Button, Card, Form, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Flex from 'components/common/Flex';
import Avatar from 'components/common/Avatar';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import paths from 'routes/paths';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import SubtleBadge from 'components/common/SubtleBadge';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import PropTypes from 'prop-types';
import FalconCloseButton from 'components/common/FalconCloseButton';
import { sendNotification } from 'nursd-dashboard/api/webNotification';
import { postNurseNotification } from 'nursd-dashboard/api/nurseNotification';
import { useAuth } from 'nursd-dashboard/context/AuthContext';
import IconButton from 'components/common/IconButton';
import AnnouncementTableHeader from './AnnouncementTableHeader';
import { socket } from 'nursd-dashboard/api/socket';
import { toast } from 'react-toastify';

const AnnouncementTable = ({
  data,
  license,
  options,
  setSearchText,
  setLicense
}) => {
  const { authToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showModel, setShowModel] = useState(false);
  const [obj, setObj] = useState(undefined);
  const [type, setType] = useState('');
  const [typeContent, setTypeContent] = useState({
    type: 'Single',
    array: []
  });
  const [content, setContent] = useState({
    title: '',
    message: '',
    url: ''
  });

  const handleClose = () => {
    setShowModel(false);
    setObj(undefined);
    setContent({
      title: '',
      message: '',
      url: ''
    });
    setType('');
    setTypeContent({
      type: 'Single',
      array: []
    });
    setLoading(false);
  };

  const handleChange = event => {
    setContent(prevState => {
      return { ...prevState, [event.target.name]: event.target.value };
    });
  };

  const handleShow = (e, t, tc, array) => {
    setType(t);
    setShowModel(true);
    setObj(e);
    setTypeContent({
      type: tc,
      array: array
    });
  };

  const send = async () => {
    setLoading(true);
    await postNurseNotification(
      authToken,
      obj?.id,
      '',
      content.title,
      content.message,
      'NotificationScreen',
      {
        id: ''
      },
      false,
      false,
      'blue',
      content.url
    );
    socket?.emit('tableNotificationInsert', {
      table: 'NurseNotificationTable',
      id: obj?.id
    });
    if (obj?.mobileId) {
      sendNotification(authToken, {
        expoPushToken: obj?.mobileId,
        title: content.title,
        body: content.message,
        data: { screen: 'NotificationScreen' }
      });
    }
    toast.success(`Successfully sent.`, {
      theme: 'colored'
    });
    handleClose();
  };

  const sendAll = async () => {
    setLoading(true);
    for (let i = 0; i < data?.length; i++) {
      console.log(data[i]?.mobileId);
      await postNurseNotification(
        authToken,
        data[i]?.id,
        '',
        content.title,
        content.message,
        'NotificationScreen',
        {
          id: ''
        },
        false,
        false,
        'blue',
        content.url
      );
      socket?.emit('tableNotificationInsert', {
        table: 'NurseNotificationTable',
        id: data[i]?.id
      });
      if (data[i]?.mobileId) {
        sendNotification(authToken, {
          expoPushToken: data[i]?.mobileId,
          title: content.title,
          body: content.message,
          data: { screen: 'NotificationScreen' }
        });
      }
    }
    toast.success(`Successfully sent.`, {
      theme: 'colored'
    });
    handleClose();
  };

  const sendSelected = async array => {
    setLoading(true);
    // console.log(array);
    for (let i = 0; i < array?.length; i++) {
      console.log(array[i]?.id);
      await postNurseNotification(
        authToken,
        array[i]?.id,
        '',
        content.title,
        content.message,
        'NotificationScreen',
        {
          id: ''
        },
        false,
        false,
        'blue',
        content.url
      );
      socket?.emit('tableNotificationInsert', {
        table: 'NurseNotificationTable',
        id: array[i]?.id
      });
      if (array[i]?.mobileId) {
        sendNotification(authToken, {
          expoPushToken: array[i]?.mobileId,
          title: content.title,
          body: content.message,
          data: { screen: 'NotificationScreen' }
        });
      }
    }
    toast.success(`Successfully sent.`, {
      theme: 'colored'
    });
    handleClose();
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Name',
      meta: {
        headerProps: { className: 'pe-1 text-900' },
        cellProps: {
          className: 'py-2'
        }
      },
      cell: ({ row: { original } }) => {
        const { id, name, profileImage, primaryLicenseType } = original;
        const { emailId } = original.original;
        return (
          <Link to={paths.nursd_profile(id, 'profile')}>
            <Flex alignItems="center">
              {profileImage ? (
                <Avatar src={profileImage} size="xl" className="me-2" />
              ) : (
                <Avatar size="xl" name={name} className="me-2" />
              )}
              <div className="flex-1">
                <h5 className="mb-0 fs-10">
                  {name}-({primaryLicenseType})
                </h5>
                <a href={`mailto:${emailId}`}>{emailId}</a>
              </div>
            </Flex>
          </Link>
        );
      }
    },
    {
      accessorKey: 'phoneNumber',
      header: 'Weather',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original } }) => {
        const { weather } = original;
        const temperatureInKelvin = weather?.main?.temp;
        const temperatureInCelsius = (temperatureInKelvin - 273.15).toFixed(1);

        return (
          <h5 className="mb-0 fs-10">
            Temperature:{' '}
            {temperatureInCelsius < 0
              ? 'extremely cold'
              : temperatureInCelsius < 10
              ? 'very cold'
              : temperatureInCelsius < 20
              ? 'cold'
              : temperatureInCelsius < 25
              ? 'cool'
              : temperatureInCelsius < 30
              ? 'mild'
              : temperatureInCelsius < 35
              ? 'warm'
              : 'hot'}
            ({((weather?.main?.temp - 273.15) * 1.8 + 32).toFixed(1)}°F) ,
            Humidity: {weather?.main?.humidity} , Description:{' '}
            {weather?.weather[0].description}
          </h5>
        );
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: false,
      meta: {
        headerProps: { className: 'text-900' },
        cellProps: { className: '' }
      },
      cell: ({ row: { original } }) => {
        const { nurseLoginControl, nurseAppAccessControl } = original.original;
        return (
          <Flex>
            <SubtleBadge pill bg={nurseLoginControl ? 'success' : 'danger'}>
              {nurseLoginControl ? 'Active' : 'Deactive'}
            </SubtleBadge>
            {nurseLoginControl && (
              <SubtleBadge
                className="mx-2"
                pill
                bg={nurseAppAccessControl ? 'danger' : 'success'}
              >
                {nurseAppAccessControl ? 'Suspend' : 'Unsuspend'}
              </SubtleBadge>
            )}
          </Flex>
        );
      }
    },
    {
      accessorKey: 'none',
      header: '',
      enableSorting: false,
      meta: {
        cellProps: {
          className: 'text-end'
        }
      },
      cell: ({ row: { original } }) => {
        return (
          <Flex justifyContent={'center'}>
            <IconButton
              variant="falcon-success"
              size="sm"
              icon="bell"
              transform="shrink-3"
              className="mx-2"
              onClick={() => {
                handleShow(original.original, 'Announcement', 'Single', []);
              }}
            >
              <span className="d-none text-success d-sm-inline-block ms-1">
                Announcement
              </span>
            </IconButton>
            <IconButton
              variant="falcon-danger"
              size="sm"
              icon="bolt"
              transform="shrink-3"
              className="mx-2"
              onClick={() => {
                handleShow(original.original, 'Weather', 'Single', []);
              }}
            >
              <span className="d-none text-danger d-sm-inline-block ms-1">
                Weather
              </span>
            </IconButton>
          </Flex>
        );
      }
    }
  ];

  const handleClick = async () => {
    if (typeContent?.type === 'All') {
      sendAll();
    } else if (typeContent?.type === 'Selected') {
      sendSelected(typeContent?.array);
    } else {
      send();
    }
  };

  const table = useAdvanceTable({
    data: data,
    columns,
    selection: true,
    sortable: true,
    pagination: true,
    perPage: 10
  });

  return (
    <>
      <AdvanceTableProvider {...table}>
        <AnnouncementTableHeader
          handleShow={handleShow}
          license={license}
          options={options}
          setSearchText={setSearchText}
          setLicense={setLicense}
        />
        <Card className="my-3">
          <Card.Body className="p-0">
            <AdvanceTable
              headerClassName="bg-200 text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                size: 'sm',
                striped: true,
                className: 'fs-10 mb-0 overflow-hidden'
              }}
            />
          </Card.Body>
          <Card.Footer>
            <div className="mt-3">
              <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
            </div>
          </Card.Footer>
        </Card>
      </AdvanceTableProvider>
      <Modal
        show={showModel}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title>
            {type === 'Weather' ? 'Weather Alert' : type}
            {/* {typeContent?.type} */}
          </Modal.Title>
          <FalconCloseButton onClick={handleClose} />
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2" controlId="title">
            <Form.Label className="fs-9">Title</Form.Label>
            <Form.Control
              rows={3}
              value={content?.ti}
              name="title"
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Group className="mb-2" controlId="message">
            <Form.Label className="fs-9">Message</Form.Label>
            <Form.Control
              value={content?.message}
              as="textarea"
              rows={3}
              name="message"
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Group className="mb-2" controlId="url">
            <Form.Label className="fs-9">URL</Form.Label>
            <Form.Control
              rows={3}
              value={content?.url}
              name="url"
              onChange={handleChange}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button variant="primary" onClick={() => handleClick()}>
            {loading ? 'Loading...' : 'Send'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

AnnouncementTable.propTypes = {
  data: PropTypes.any,
  license: PropTypes.any,
  options: PropTypes.any,
  setSearchText: PropTypes.any,
  setLicense: PropTypes.any
};

export default AnnouncementTable;
